import CollisionService from "./collision_service";
import SceneObject from "../scene/sceneobject";
import {
  GROUND_NODE_ID,
  JumpAnimKind,
  JumpEdge,
  JumpNode,
  PuppyLocation,
} from "./jump_graph_types";

const MAX_HOP_XZ = 1.4; //was 1.6
const MAX_UP = 0.6;
const MAX_DOWN = 1.4;
const FLAT_THRESH = 0.12;
const LOW_DROP_THRESH = 0.5;
const MAX_JUMP_UP_FROM_GROUND = 0.45;
const MAX_DROP_TO_GROUND = 0.45; // was 1.5 — only short caps can drop directly
const UP_PENALTY = 1.7;


export default class JumpGraphService {
  private nodes: Map<string, JumpNode> = new Map();
  private edges: JumpEdge[] = [];
  private edges_by_from: Map<string, JumpEdge[]> = new Map();
  private highlighted_path: JumpEdge[] | null = null;

  set_highlighted_path(path: JumpEdge[] | null): void 
  {
     this.highlighted_path = path;
  }

  get_highlighted_path(): JumpEdge[] | null 
  {
    return this.highlighted_path;
  }

  build(objects: SceneObject[], _collision: CollisionService): void {
    this.nodes.clear();
    this.edges = [];
    this.edges_by_from.clear();

    this.nodes.set(GROUND_NODE_ID, {
      id: GROUND_NODE_ID,
      kind: "ground",
      position: new Float32Array([0, 0, 0]),
      surface_radius: 0,
      source_object: null,
    });

    for (const obj of objects) {
      const v = obj.collisionVolumes[0];
      if (!v) continue;

      const aabb = v.getWorldAabb();
      if (!aabb) continue;

      const cx = (aabb.min[0] + aabb.max[0]) * 0.5;
      const cz = (aabb.min[2] + aabb.max[2]) * 0.5;
      const top_y = aabb.max[1];

      const half_x = (aabb.max[0] - aabb.min[0]) * 0.5;
      const half_z = (aabb.max[2] - aabb.min[2]) * 0.5;

      const id = obj.key;

      this.nodes.set(id, {
        id,
        kind: "object",
        position: new Float32Array([cx, top_y, cz]),
        surface_radius: Math.min(half_x, half_z),
        source_object: obj,
      });
    }

    this.generate_edges();

    // in build(), after generating edges:
   console.log("gererated edges");

    const orphans = Array.from(this.nodes.values())
  .filter(n => n.kind === "object")
  .filter(n => !this.edges.some(e =>
    (e.from === n.id && this.get_node(e.to)?.kind === "object") ||
    (e.to === n.id && this.get_node(e.from)?.kind === "object")
  ));
console.log("[jump_graph] cap-isolated nodes (only connect to ground):",
  orphans.map(o => o.id));

  
  /*  console.log("[jump_graph] nodes:", this.nodes.size);
for (const n of this.nodes.values()) {
  if (n.kind === "ground") continue;
  console.log(`  node ${n.id}  pos=(${n.position[0].toFixed(2)}, ${n.position[1].toFixed(2)}, ${n.position[2].toFixed(2)})  r=${n.surface_radius.toFixed(2)}`);
}
console.log("[jump_graph] edges:", this.edges.length);
for (const e of this.edges) {
  console.log(`  ${e.from} -> ${e.to}  h=${e.horizontal.toFixed(2)}  v=${e.vertical.toFixed(2)}  anim=${e.anim}`);
}*/
  }

  private generate_edges(): void {
    const list = Array.from(this.nodes.values());
    const ground = this.nodes.get(GROUND_NODE_ID)!;

    for (const a of list) {
      if (a.kind !== "object") continue;

      if (a.position[1] <= MAX_JUMP_UP_FROM_GROUND) {
        this.add_edge(
          ground.id,
          a.id,
          this.horiz(ground.position, a.position),
          a.position[1],
          "ground_to_object"
        );
      }
      if (a.position[1] <= MAX_DROP_TO_GROUND) {
        this.add_edge(
          a.id,
          ground.id,
          this.horiz(a.position, ground.position),
          -a.position[1],
          "object_to_ground"
        );
      }

      for (const b of list) {
        if (a === b || b.kind !== "object") continue;

        const horizontal = this.horiz(a.position, b.position);
        if (horizontal > MAX_HOP_XZ) continue;

        const vertical = b.position[1] - a.position[1];
        const anim = this.classify(horizontal, vertical);
        if (!anim) continue;

        this.add_edge(a.id, b.id, horizontal, vertical, anim);
      }
    }
  }

  private horiz(p: Float32Array, q: Float32Array): number {
    const dx = q[0] - p[0];
    const dz = q[2] - p[2];
    return Math.sqrt(dx * dx + dz * dz);
  }

  private classify(h: number, v: number): JumpAnimKind | null {
    if (Math.abs(v) <= FLAT_THRESH) return "object_to_object_across";
    if (v > 0) {
      if (v <= MAX_UP) return "object_to_object_up";
      return null;
    }
    const drop = -v;
    if (drop <= LOW_DROP_THRESH) return "object_to_object_down_low";
    if (drop <= MAX_DOWN) return "object_to_object_down_high";
    return null;
  }

  private add_edge(
    from: string,
    to: string,
    horizontal: number,
    vertical: number,
    anim: JumpAnimKind
  ): void {
    const edge: JumpEdge = { from, to, horizontal, vertical, anim };
    this.edges.push(edge);

    let bucket = this.edges_by_from.get(from);
    if (!bucket) {
      bucket = [];
      this.edges_by_from.set(from, bucket);
    }
    bucket.push(edge);
  }

  get_nodes(): JumpNode[] { return Array.from(this.nodes.values()); }
  get_edges(): JumpEdge[] { return this.edges; }
  get_node(id: string): JumpNode | null { return this.nodes.get(id) ?? null; }

  find_object_node_under_world_point(point: Float32Array, max_radius: number): JumpNode | null {
    let best: JumpNode | null = null;
    let best_d = Infinity;

    for (const n of this.nodes.values()) {
      if (n.kind !== "object") continue;

      const dx = point[0] - n.position[0];
      const dz = point[2] - n.position[2];
      const d = Math.sqrt(dx * dx + dz * dz);
      const r = Math.max(n.surface_radius, max_radius);

      if (d <= r && d < best_d) {
        best = n;
        best_d = d;
      }
    }
    return best;
  }

  plan_path(from: PuppyLocation, to_node_id: string): JumpEdge[] | null {
    const start_id = from.kind === "ground" ? GROUND_NODE_ID : from.node_id;
    if (start_id === to_node_id) return [];
    return this.a_star(start_id, to_node_id);
  }

  private a_star(start: string, goal: string): JumpEdge[] | null {
    const open = new Set<string>([start]);
    const came_from = new Map<string, JumpEdge>();
    const g_score = new Map<string, number>([[start, 0]]);
    const f_score = new Map<string, number>([[start, this.h(start, goal)]]);

    while (open.size) {
      let current: string | null = null;
      let current_f = Infinity;
      for (const id of open) {
        const f = f_score.get(id) ?? Infinity;
        if (f < current_f) { current = id; current_f = f; }
      }
      if (!current) return null;

      if (current === goal) {
        const path: JumpEdge[] = [];
        let c = current;
        while (came_from.has(c)) {
          const e = came_from.get(c)!;
          path.unshift(e);
          c = e.from;
        }
        return path;
      }

      open.delete(current);

      const out = this.edges_by_from.get(current) ?? [];
      for (const e of out) {
        const tentative = (g_score.get(current) ?? Infinity) + this.edge_cost(e);
        if (tentative < (g_score.get(e.to) ?? Infinity)) {
          came_from.set(e.to, e);
          g_score.set(e.to, tentative);
          f_score.set(e.to, tentative + this.h(e.to, goal));
          open.add(e.to);
        }
      }
    }
    return null;
  }

  private edge_cost(e: JumpEdge): number {
    return e.horizontal + Math.max(0, e.vertical) * UP_PENALTY;
  }

  private h(a_id: string, b_id: string): number {
    const a = this.nodes.get(a_id)!;
    const b = this.nodes.get(b_id)!;
    const dx = a.position[0] - b.position[0];
    const dy = a.position[1] - b.position[1];
    const dz = a.position[2] - b.position[2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
}